/**
 * ReviewStrip — the bench, emptied on a phone (/feed?bench=1 for an admin; Sprint 2 item 6).
 *
 * The feed is the bench (bench_feed(), oldest first) and this strip sits between the card and
 * the bottom bar, never over the card's surface: the viewport is shortened by the strip's height
 * in bench mode (index.css, .app.bench). For the card on screen it says who made it, the maker's
 * own 'Made with' word and the fit check's phrase, and how the bench stands (bench_summary()).
 * Live and Hide call card_set_status, the one door to live, with fixed reasons (src/lib/review.ts),
 * so every flip is logged with via='rpc', this admin and a reason the maker reads back. Hide asks
 * for its one word first. After a flip the feed moves on to the next card.
 */
import { For, Show, createEffect, createSignal, on } from 'solid-js';
import { setCardStatus, type BenchCard } from '../api/cards';
import { HIDE_REASONS, LIVE_REASON, benchCountLine, benchWho, doneLine, hideReason, type HideReason } from '../lib/review';
import type { BenchSummary } from '../api/stats';

export type ReviewOutcome = { status: 'live' } | { status: 'hidden'; word: HideReason };

export interface ReviewStripProps {
  /** The bench card on screen; null on the end card. */
  card: BenchCard | null;
  /** What this page already did to that card, if anything. */
  outcome: ReviewOutcome | null;
  summary: BenchSummary | null;
  /** Bench cards on this page not yet flipped. */
  left: number;
  onDone: (id: string, outcome: ReviewOutcome) => void;
}

export function ReviewStrip(props: ReviewStripProps) {
  const [busy, setBusy] = createSignal(false);
  const [hiding, setHiding] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);

  // Another card on screen starts clean: no half-chosen reason, no old error.
  createEffect(
    on(
      () => props.card?.meta.id,
      () => {
        setHiding(false);
        setError(null);
      },
    ),
  );

  const flip = async (outcome: ReviewOutcome) => {
    const card = props.card;
    if (!card || busy()) return;
    setBusy(true);
    setError(null);
    const r = await setCardStatus(card.meta.id, outcome.status, outcome.status === 'live' ? LIVE_REASON : hideReason(outcome.word));
    setBusy(false);
    if (!r.ok) {
      setError(r.reason);
      return;
    }
    setHiding(false);
    props.onDone(card.meta.id, outcome);
  };

  return (
    <section class="review-strip" data-ui data-testid="review-strip" aria-label="Review the bench">
      <Show
        when={props.card}
        fallback={
          <p class="review-line" data-testid="review-empty">
            {props.left ? `${props.left} still waiting above.` : 'The bench is empty.'} <span class="review-count">{benchCountLine(props.summary, props.left)}</span>
          </p>
        }
      >
        {(card) => (
          <>
            <p class="review-line" data-testid="review-who">
              {benchWho(card())}
            </p>
            <p class="review-count" data-testid="review-count">
              {benchCountLine(props.summary, props.left)}
            </p>
            <Show when={error()}>
              <p class="review-error" role="alert" data-testid="review-error">
                {error()}
              </p>
            </Show>
            <Show
              when={hiding()}
              fallback={
                <div class="review-actions">
                  <Show when={props.outcome}>
                    {(o) => (
                      <span class="review-done" data-testid="review-done">
                        {doneLine(o())}
                      </span>
                    )}
                  </Show>
                  <button type="button" class="btn review-hide" disabled={busy()} onClick={() => setHiding(true)} data-testid="review-hide">
                    Hide
                  </button>
                  <button type="button" class="btn primary review-live" disabled={busy()} onClick={() => void flip({ status: 'live' })} data-testid="review-live">
                    {busy() ? '…' : 'Live'}
                  </button>
                </div>
              }
            >
              {/* Hide asks why, in one word the maker will read back. */}
              <div class="review-actions reasons" role="group" aria-label="Why hide it">
                <For each={HIDE_REASONS}>
                  {(w) => (
                    <button type="button" class="btn" disabled={busy()} onClick={() => void flip({ status: 'hidden', word: w })} data-testid={`review-reason-${w}`}>
                      {w}
                    </button>
                  )}
                </For>
                <button type="button" class="btn quiet" disabled={busy()} onClick={() => setHiding(false)} data-testid="review-cancel">
                  Cancel
                </button>
              </div>
            </Show>
          </>
        )}
      </Show>
    </section>
  );
}
